import {
  Activity,
  ArrowRight,
  BadgeCheck,
  BarChart3,
  BookOpen,
  Building2,
  Check,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Coins,
  Database,
  FileText,
  Gauge,
  Globe2,
  HandCoins,
  Landmark,
  Layers3,
  LayoutDashboard,
  LifeBuoy,
  LockKeyhole,
  Menu,
  MessageSquare,
  ShieldCheck,
  Sparkles,
  TrendingUp,
  Users,
  WalletCards,
  X
} from "lucide-react";
import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";

type Route =
  | "/"
  | "/products/earn"
  | "/products/borrow"
  | "/products/swap"
  | "/products/buy"
  | "/products/send"
  | "/institutions"
  | "/trust"
  | "/learn"
  | "/support"
  | "/company"
  | "/legal"
  | "/admin";

type ProductPage = {
  key: string;
  route: Route;
  icon: ReactNode;
  title: string;
  eyebrow: string;
  summary: string;
  details: string[];
  workflow: string[];
  risks: string[];
};

type ApiSummary = {
  ok: boolean;
  waitlistCount: number;
  interestCount: number;
  latestEvents: Array<{ event_type: string; severity: string; message: string; created_at: string }>;
};

const navItems = [
  { label: "Products", href: "/products/earn" },
  { label: "Trust & Safety", href: "/trust" },
  { label: "Learn", href: "/learn" },
  { label: "Company", href: "/company" },
  { label: "Support", href: "/support" }
] satisfies Array<{ label: string; href: Route }>;

const productPages: ProductPage[] = [
  {
    key: "earn",
    route: "/products/earn",
    icon: <TrendingUp aria-hidden="true" />,
    title: "Earn dashboard",
    eyebrow: "Rewards preview",
    summary:
      "A controlled rewards experience with eligibility copy, rate disclosure slots, consent checkpoints, and clear withdrawal language.",
    details: ["Configurable asset table", "Reward source notes", "Eligibility by region", "Consent version tracking"],
    workflow: ["Choose supported asset", "Review estimated rewards", "Confirm risk notes", "Track dashboard state"],
    risks: ["Rewards are not guaranteed", "Availability can vary by region", "Counterparty and liquidity risk must be disclosed"]
  },
  {
    key: "borrow",
    route: "/products/borrow",
    icon: <HandCoins aria-hidden="true" />,
    title: "Borrow flow",
    eyebrow: "Collateral education",
    summary:
      "A borrowing journey that explains collateral, LTV, repayment timing, margin call conditions, and liquidation risk before submission.",
    details: ["Loan calculator surface", "Collateral requirement cards", "Repayment timeline", "Liquidation threshold copy"],
    workflow: ["Pick collateral", "Estimate LTV", "Review terms", "Confirm repayment plan"],
    risks: ["Collateral can lose value", "Loans need jurisdiction checks", "Late repayment and liquidation terms require legal review"]
  },
  {
    key: "swap",
    route: "/products/swap",
    icon: <Coins aria-hidden="true" />,
    title: "Swap experience",
    eyebrow: "Quote and settlement",
    summary:
      "A compliant swap UX with quote preview, fee and spread disclosure, expiry timing, and transaction state messaging.",
    details: ["Quote preview", "Fee and spread slots", "Quote expiry", "Settlement status"],
    workflow: ["Select assets", "Preview quote", "Review spread", "Confirm swap state"],
    risks: ["Prices can change quickly", "Network costs may apply", "Execution depends on liquidity and compliance rules"]
  },
  {
    key: "buy",
    route: "/products/buy",
    icon: <WalletCards aria-hidden="true" />,
    title: "Buy crypto",
    eyebrow: "Funding routes",
    summary:
      "A buy-flow blueprint with provider fees, settlement timing, payment methods, and KYC requirements surfaced before checkout.",
    details: ["Payment method matrix", "Provider fee labels", "Settlement time notes", "KYC checkpoint"],
    workflow: ["Select payment method", "Confirm provider fees", "Complete identity step", "Receive settlement status"],
    risks: ["Third-party fees may apply", "Payments can be delayed", "Not every region or payment method is supported"]
  },
  {
    key: "send",
    route: "/products/send",
    icon: <Globe2 aria-hidden="true" />,
    title: "FireBank Send",
    eyebrow: "Transfer workflow",
    summary:
      "A P2P transfer concept that replaces Celsius-specific naming with FireBank Send and clearer address confirmation states.",
    details: ["Recipient verification", "Address confirmation", "Network fee preview", "Transfer status"],
    workflow: ["Choose recipient", "Verify address", "Review network", "Send and track"],
    risks: ["Blockchain transfers can be irreversible", "Recipient data must be verified", "Network fees and timing can vary"]
  }
];

const routeInventory = [
  "Home",
  "Earn",
  "Borrow",
  "Swap",
  "Buy",
  "Send",
  "Institutions",
  "Trust Center",
  "Learn",
  "Support",
  "Legal",
  "Admin"
];

const adminFallback: ApiSummary = {
  ok: true,
  waitlistCount: 0,
  interestCount: 0,
  latestEvents: [
    {
      event_type: "preview",
      severity: "info",
      message: "Admin console loaded with local fallback while API data is unavailable.",
      created_at: new Date().toISOString()
    }
  ]
};

function App() {
  const [route, setRoute] = useState<Route>(() => normalizeRoute(window.location.pathname));
  const [menuOpen, setMenuOpen] = useState(false);
  const [cookiesChoice, setCookiesChoice] = useState<"accepted" | "declined" | null>(null);

  useEffect(() => {
    const onPopState = () => setRoute(normalizeRoute(window.location.pathname));
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    document.title = getRouteTitle(route);
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [route]);

  function navigate(href: Route) {
    window.history.pushState(null, "", href);
    setRoute(href);
    setMenuOpen(false);
  }

  return (
    <div className="site-shell">
      <Header route={route} menuOpen={menuOpen} setMenuOpen={setMenuOpen} navigate={navigate} />
      <main>
        {route === "/" && <HomePage navigate={navigate} />}
        {productPages.map((product) =>
          route === product.route ? <ProductDetailPage key={product.key} product={product} navigate={navigate} /> : null
        )}
        {route === "/institutions" && <InstitutionsPage navigate={navigate} />}
        {route === "/trust" && <TrustPage navigate={navigate} />}
        {route === "/learn" && <LearnPage navigate={navigate} />}
        {route === "/support" && <SupportPage />}
        {route === "/company" && <CompanyPage navigate={navigate} />}
        {route === "/legal" && <LegalPage />}
        {route === "/admin" && <AdminPage navigate={navigate} />}
      </main>
      <Footer navigate={navigate} />
      {!cookiesChoice && (
        <div className="cookie-bar" role="region" aria-label="Cookie notice">
          <p>We use essential cookies only for this preview experience.</p>
          <div>
            <button type="button" className="secondary-button" onClick={() => setCookiesChoice("declined")}>
              Decline
            </button>
            <button type="button" className="accent-button" onClick={() => setCookiesChoice("accepted")}>
              Accept
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Header({
  route,
  menuOpen,
  setMenuOpen,
  navigate
}: {
  route: Route;
  menuOpen: boolean;
  setMenuOpen: (value: boolean) => void;
  navigate: (href: Route) => void;
}) {
  return (
    <header className="topbar">
      <button className="brand brand-button" type="button" onClick={() => navigate("/")} aria-label="FireBank home">
        <span className="brand-mark">F</span>
        <span>FireBank</span>
      </button>
      <nav className="desktop-nav" aria-label="Primary navigation">
        {navItems.map((item) => (
          <button key={item.href} type="button" className={route === item.href ? "active-nav" : ""} onClick={() => navigate(item.href)}>
            {item.label}
          </button>
        ))}
      </nav>
      <div className="topbar-actions">
        <button className="ghost-link" type="button" onClick={() => navigate("/admin")}>
          Admin
        </button>
        <button className="primary-small" type="button" onClick={() => navigate("/support")}>
          Join preview
        </button>
        <button
          className="icon-button menu-button"
          type="button"
          aria-label={menuOpen ? "Close menu" : "Open menu"}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
        </button>
      </div>
      {menuOpen && (
        <nav className="mobile-nav" aria-label="Mobile navigation">
          {[...navItems, { label: "Admin", href: "/admin" as Route }].map((item) => (
            <button key={item.href} type="button" onClick={() => navigate(item.href)}>
              {item.label}
            </button>
          ))}
        </nav>
      )}
    </header>
  );
}

function HomePage({ navigate }: { navigate: (href: Route) => void }) {
  return (
    <>
      <section className="hero-section" aria-labelledby="hero-title">
        <div className="hero-copy">
          <p className="eyebrow">
            <Sparkles aria-hidden="true" />
            Public preview for a digital asset home base
          </p>
          <h1 id="hero-title">Your safer digital asset</h1>
          <p className="hero-lede">
            FireBank combines a polished crypto banking interface with clear education, visible risk checkpoints,
            backend-backed waitlist storage, and a deployment-ready Cloudflare architecture.
          </p>
          <WaitlistForm />
          <p className="form-note">
            Preview signups are non-custodial. FireBank does not accept deposits, execute trades, or offer lending
            products on this site.
          </p>
        </div>
        <HeroVisual />
      </section>
      <section className="trust-strip" aria-label="FireBank operating principles">
        <div>
          <BadgeCheck aria-hidden="true" />
          <span>Brand-safe rebuild</span>
        </div>
        <div>
          <LockKeyhole aria-hidden="true" />
          <span>Risk-first UX</span>
        </div>
        <div>
          <Database aria-hidden="true" />
          <span>D1-backed backend</span>
        </div>
      </section>
      <section className="section">
        <div className="section-heading">
          <p className="eyebrow">Complete site map</p>
          <h2>Not a one-pager anymore: public site, product pages, trust center, legal, support, and admin.</h2>
          <p>
            The May 11 archive informs the depth of the information architecture. The June 15 hero informs only the
            first-screen visual rhythm.
          </p>
        </div>
        <div className="product-grid">
          {productPages.slice(0, 4).map((product) => (
            <article className="product-card" key={product.key}>
              <div className="card-icon">{product.icon}</div>
              <p>{product.eyebrow}</p>
              <h3>{product.title}</h3>
              <span>{product.summary}</span>
              <button className="text-cta button-reset" type="button" onClick={() => navigate(product.route)}>
                Open page <ChevronRight aria-hidden="true" />
              </button>
            </article>
          ))}
        </div>
      </section>
      <section className="split-band">
        <div>
          <p className="eyebrow">Back office</p>
          <h2>The backend is now part of the product surface.</h2>
          <p>
            The admin console tracks waitlist counts, product interest, content readiness, migration state, and
            operational events through Cloudflare Pages Functions and D1.
          </p>
          <button className="text-cta button-reset" type="button" onClick={() => navigate("/admin")}>
            Open admin console <ChevronRight aria-hidden="true" />
          </button>
        </div>
        <AdminPreviewPanel />
      </section>
      <section className="section compact">
        <div className="section-heading">
          <p className="eyebrow">Route inventory</p>
          <h2>Every important route has a defined purpose.</h2>
        </div>
        <div className="route-grid">
          {routeInventory.map((routeName) => (
            <div className="route-pill" key={routeName}>
              <Layers3 aria-hidden="true" />
              {routeName}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

function ProductDetailPage({ product, navigate }: { product: ProductPage; navigate: (href: Route) => void }) {
  return (
    <PageShell
      eyebrow={product.eyebrow}
      title={product.title}
      intro={product.summary}
      actionLabel="Open admin tracking"
      action={() => navigate("/admin")}
      icon={product.icon}
    >
      <div className="content-grid">
        <InfoPanel title="Feature scope" items={product.details} icon={<Gauge aria-hidden="true" />} />
        <InfoPanel title="User workflow" items={product.workflow} icon={<Activity aria-hidden="true" />} />
        <InfoPanel title="Risk notes" items={product.risks} icon={<ShieldCheck aria-hidden="true" />} />
      </div>
      <section className="detail-band">
        <div>
          <p className="eyebrow">Availability first</p>
          <h2>Product copy is written as a controlled preview, not a live financial promise.</h2>
          <p>
            Every page has room for jurisdiction, disclosure, rate date, provider, and consent copy. This keeps the
            IA rich while avoiding Celsius-style unsupported claims.
          </p>
        </div>
        <button className="primary-large" type="button" onClick={() => navigate("/support")}>
          Check availability <ArrowRight aria-hidden="true" />
        </button>
      </section>
    </PageShell>
  );
}

function InstitutionsPage({ navigate }: { navigate: (href: Route) => void }) {
  return (
    <PageShell
      eyebrow="Institutions"
      title="A quieter path for diligence, treasury, and support ownership."
      intro="Institutional users need documentation, eligibility review, treasury reporting, and account ownership before product access."
      actionLabel="View admin workflow"
      action={() => navigate("/admin")}
      icon={<Building2 aria-hidden="true" />}
    >
      <div className="content-grid">
        <InfoPanel title="Onboarding" items={["Entity profile", "Beneficial ownership", "Policy review", "Approval state"]} icon={<Landmark aria-hidden="true" />} />
        <InfoPanel title="Treasury" items={["Portfolio view", "Statement exports", "Settlement notes", "Role access"]} icon={<BarChart3 aria-hidden="true" />} />
        <InfoPanel title="Controls" items={["Risk acceptance", "Document center", "Support owner", "Audit trail"]} icon={<FileText aria-hidden="true" />} />
      </div>
    </PageShell>
  );
}

function TrustPage({ navigate }: { navigate: (href: Route) => void }) {
  return (
    <PageShell
      eyebrow="Trust & Safety"
      title="Make safety visible before asking users to join."
      intro="The Trust Center replaces vague security language with concrete controls, disclosure slots, and audit-ready operating notes."
      actionLabel="Read legal notes"
      action={() => navigate("/legal")}
      icon={<ShieldCheck aria-hidden="true" />}
    >
      <div className="content-grid">
        <InfoPanel title="Account safety" items={["2FA direction", "Withdrawal controls", "Session visibility", "Device review"]} icon={<LockKeyhole aria-hidden="true" />} />
        <InfoPanel title="Transparency" items={["Proof methodology placeholder", "Reserve report slot", "Status page slot", "Last updated labels"]} icon={<Globe2 aria-hidden="true" />} />
        <InfoPanel title="Incident response" items={["Severity levels", "Customer notices", "Support routing", "Operational event log"]} icon={<Activity aria-hidden="true" />} />
      </div>
    </PageShell>
  );
}

function LearnPage({ navigate }: { navigate: (href: Route) => void }) {
  return (
    <PageShell
      eyebrow="Learn"
      title="Education pages for users who need clarity before action."
      intro="Learn collects rates education, fees, glossary, risk concepts, and product walkthroughs into one route."
      actionLabel="Contact support"
      action={() => navigate("/support")}
      icon={<BookOpen aria-hidden="true" />}
    >
      <div className="content-grid">
        <InfoPanel title="Guides" items={["How rewards work", "How LTV works", "Swap quote basics", "Crypto transfer safety"]} icon={<BookOpen aria-hidden="true" />} />
        <InfoPanel title="Glossary" items={["APY", "APR", "LTV", "Collateral", "Slippage", "Custody"]} icon={<Layers3 aria-hidden="true" />} />
        <InfoPanel title="FAQ" items={["Is FireBank live?", "Does this site hold funds?", "Which products are preview-only?", "How are signups stored?"]} icon={<MessageSquare aria-hidden="true" />} />
      </div>
    </PageShell>
  );
}

function SupportPage() {
  return (
    <PageShell
      eyebrow="Support"
      title="Support that routes users by intent, not by guesswork."
      intro="Support covers waitlist questions, product availability, legal requests, technical issues, and institutional handoff."
      actionLabel="Email support"
      action={() => {
        window.location.href = "mailto:hello@firebank.example";
      }}
      icon={<LifeBuoy aria-hidden="true" />}
    >
      <div className="content-grid">
        <InfoPanel title="Customer care" items={["Waitlist support", "Availability questions", "Account preview help", "Accessibility feedback"]} icon={<LifeBuoy aria-hidden="true" />} />
        <InfoPanel title="Technical" items={["API health", "Deployment status", "Cloudflare Pages", "D1 migration notes"]} icon={<Database aria-hidden="true" />} />
        <InfoPanel title="Response model" items={["Triage first", "No financial advice", "Escalate legal questions", "Record operational events"]} icon={<Clock3 aria-hidden="true" />} />
      </div>
    </PageShell>
  );
}

function CompanyPage({ navigate }: { navigate: (href: Route) => void }) {
  return (
    <PageShell
      eyebrow="Company"
      title="A FireBank story separated from Celsius history."
      intro="The company route describes FireBank's product principles without importing old team claims, awards, media logos, or unverified numbers."
      actionLabel="View source policy"
      action={() => navigate("/legal")}
      icon={<Users aria-hidden="true" />}
    >
      <div className="content-grid">
        <InfoPanel title="Mission" items={["Clear access", "Risk-first education", "Transparent workflows", "Operational discipline"]} icon={<Sparkles aria-hidden="true" />} />
        <InfoPanel title="Operating principles" items={["No copied brand claims", "No unsupported APY", "No false custody claims", "No legal text reuse"]} icon={<ShieldCheck aria-hidden="true" />} />
        <InfoPanel title="Roadmap" items={["Public site", "Backend waitlist", "Admin console", "Auth and product simulations"]} icon={<LayoutDashboard aria-hidden="true" />} />
      </div>
    </PageShell>
  );
}

function LegalPage() {
  return (
    <PageShell
      eyebrow="Legal and risk"
      title="Reference structure only. Legal terms must be reviewed before live finance use."
      intro="This page is intentionally conservative. It keeps risk, privacy, terms, and availability routes visible without copying Celsius legal content."
      actionLabel="Download risk brief"
      action={() => window.print()}
      icon={<FileText aria-hidden="true" />}
    >
      <div className="content-grid">
        <InfoPanel title="Terms placeholder" items={["Preview-only site", "No custody", "No trading execution", "No financial advice"]} icon={<FileText aria-hidden="true" />} />
        <InfoPanel title="Privacy placeholder" items={["Waitlist email", "Consent version", "D1 storage", "No sale of preview data"]} icon={<Database aria-hidden="true" />} />
        <InfoPanel title="Risk disclosure" items={["Market risk", "Liquidity risk", "Counterparty risk", "Jurisdiction limits"]} icon={<ShieldCheck aria-hidden="true" />} />
      </div>
    </PageShell>
  );
}

function AdminPage({ navigate }: { navigate: (href: Route) => void }) {
  const [summary, setSummary] = useState<ApiSummary | null>(null);
  const [apiState, setApiState] = useState<"loading" | "live" | "fallback">("loading");

  useEffect(() => {
    let cancelled = false;
    fetch("/api/admin/summary")
      .then((response) => {
        if (!response.ok) throw new Error("API unavailable");
        return response.json() as Promise<ApiSummary>;
      })
      .then((data) => {
        if (!cancelled) {
          setSummary(data);
          setApiState("live");
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSummary(adminFallback);
          setApiState("fallback");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const activeSummary = summary ?? adminFallback;

  return (
    <PageShell
      eyebrow="Admin console"
      title="Back office for launch readiness, waitlist, and operations."
      intro="This backend-facing route connects to Cloudflare Pages Functions and D1 in production. It intentionally avoids exposing raw email addresses."
      actionLabel="Open public site"
      action={() => navigate("/")}
      icon={<LayoutDashboard aria-hidden="true" />}
    >
      <div className="admin-layout">
        <aside className="admin-sidebar">
          <strong>FireBank Ops</strong>
          {["Overview", "Waitlist", "Products", "Content", "Risk", "Deployments"].map((item) => (
            <span key={item}>{item}</span>
          ))}
        </aside>
        <section className="admin-main" aria-label="Admin metrics">
          <div className="metric-grid">
            <MetricCard label="API status" value={apiState === "live" ? "Live" : apiState === "loading" ? "Loading" : "Fallback"} icon={<Activity aria-hidden="true" />} />
            <MetricCard label="Waitlist" value={String(activeSummary.waitlistCount)} icon={<Users aria-hidden="true" />} />
            <MetricCard label="Interest events" value={String(activeSummary.interestCount)} icon={<BarChart3 aria-hidden="true" />} />
            <MetricCard label="Pages ready" value="12" icon={<Layers3 aria-hidden="true" />} />
          </div>
          <div className="ops-panel admin-panel">
            <div className="ops-panel-header">
              <Database aria-hidden="true" />
              <span>D1 migration status</span>
            </div>
            {["waitlist_submissions", "product_interest_events", "operational_events", "privacy-safe summary API"].map((item, index) => (
              <div className="ops-row" key={item}>
                <span>{index + 1}</span>
                <strong>{item}</strong>
                <BadgeCheck aria-hidden="true" />
              </div>
            ))}
          </div>
          <div className="timeline admin-events">
            {activeSummary.latestEvents.map((event) => (
              <article key={`${event.event_type}-${event.created_at}`}>
                <span>{event.severity}</span>
                <h3>{event.event_type}</h3>
                <p>{event.message}</p>
              </article>
            ))}
          </div>
        </section>
      </div>
    </PageShell>
  );
}

function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [interest, setInterest] = useState("general");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "fallback" | "error">("idle");
  const canSubmit = useMemo(() => /\S+@\S+\.\S+/.test(email), [email]);

  async function submitWaitlist(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!canSubmit || status === "submitting") return;
    setStatus("submitting");
    try {
      const response = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, interest, source: window.location.pathname })
      });
      if (!response.ok) throw new Error("API unavailable");
      setStatus("success");
    } catch {
      setStatus("fallback");
    }
  }

  return (
    <form className="waitlist-form expanded-form" id="waitlist" onSubmit={submitWaitlist}>
      <label className="sr-only" htmlFor="email">
        Email address
      </label>
      <input
        id="email"
        name="email"
        type="email"
        placeholder="Enter your email"
        value={email}
        onChange={(event) => {
          setEmail(event.target.value);
          setStatus("idle");
        }}
      />
      <select aria-label="Preview interest" value={interest} onChange={(event) => setInterest(event.target.value)}>
        <option value="general">General preview</option>
        <option value="earn">Earn</option>
        <option value="borrow">Borrow</option>
        <option value="institution">Institution</option>
      </select>
      <button type="submit" disabled={!canSubmit || status === "submitting"}>
        {status === "submitting"
          ? "Submitting"
          : status === "success"
            ? "Saved to backend"
            : status === "fallback"
              ? "Saved locally"
              : "Request preview"}
      </button>
      {status === "fallback" && (
        <p className="inline-status">Backend unavailable in local preview. Production uses Cloudflare D1.</p>
      )}
      {status === "success" && <p className="inline-status">Your preview request is stored in FireBank D1.</p>}
    </form>
  );
}

function PageShell({
  eyebrow,
  title,
  intro,
  actionLabel,
  action,
  icon,
  children
}: {
  eyebrow: string;
  title: string;
  intro: string;
  actionLabel: string;
  action: () => void;
  icon: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="page-shell">
      <div className="page-hero">
        <div>
          <p className="eyebrow">
            {icon}
            {eyebrow}
          </p>
          <h1>{title}</h1>
          <p>{intro}</p>
          <button className="primary-large" type="button" onClick={action}>
            {actionLabel} <ArrowRight aria-hidden="true" />
          </button>
        </div>
      </div>
      {children}
    </section>
  );
}

function InfoPanel({ title, items, icon }: { title: string; items: string[]; icon: ReactNode }) {
  return (
    <article className="info-panel">
      <div className="card-icon">{icon}</div>
      <h3>{title}</h3>
      <ul>
        {items.map((item) => (
          <li key={item}>
            <Check aria-hidden="true" />
            {item}
          </li>
        ))}
      </ul>
    </article>
  );
}

function MetricCard({ label, value, icon }: { label: string; value: string; icon: ReactNode }) {
  return (
    <article className="metric-card">
      <div className="card-icon">{icon}</div>
      <span>{label}</span>
      <strong>{value}</strong>
    </article>
  );
}

function AdminPreviewPanel() {
  return (
    <div className="ops-panel" aria-label="Admin workflow preview">
      <div className="ops-panel-header">
        <LayoutDashboard aria-hidden="true" />
        <span>Launch operations</span>
      </div>
      {["Waitlist API", "D1 migration", "Product readiness", "Risk review"].map((item, index) => (
        <div className="ops-row" key={item}>
          <span>{index + 1}</span>
          <strong>{item}</strong>
          <BadgeCheck aria-hidden="true" />
        </div>
      ))}
    </div>
  );
}

function HeroVisual() {
  return (
    <div className="hero-visual" aria-label="FireBank app preview">
      <div className="orbital-grid" aria-hidden="true" />
      <div className="token token-pink" aria-hidden="true">
        F
      </div>
      <div className="token token-blue" aria-hidden="true">
        $
      </div>
      <div className="token token-gold" aria-hidden="true">
        B
      </div>
      <div className="phone-frame">
        <div className="phone-top">
          <span />
          <strong>Welcome Maya</strong>
        </div>
        <div className="balance-card">
          <span>Portfolio preview</span>
          <strong>$248,920.40</strong>
          <small>Demo balance</small>
        </div>
        <div className="metric-row">
          <span>Rewards model</span>
          <strong>$1,842.10</strong>
        </div>
        <div className="asset-list">
          {["Bitcoin", "USD Coin", "Ethereum", "Fire token"].map((asset, index) => (
            <div className="asset-row" key={asset}>
              <span className={`asset-dot asset-${index}`} />
              <div>
                <strong>{asset}</strong>
                <small>{index === 3 ? "Preview asset" : "Portfolio asset"}</small>
              </div>
              <span>{["42.1%", "31.4%", "18.7%", "7.8%"][index]}</span>
            </div>
          ))}
        </div>
        <div className="phone-tabs">
          <span>Portfolio</span>
          <span>Transfer</span>
          <span>Borrow</span>
        </div>
      </div>
    </div>
  );
}

function Footer({ navigate }: { navigate: (href: Route) => void }) {
  return (
    <footer className="footer">
      <div>
        <button className="brand brand-button" type="button" onClick={() => navigate("/")} aria-label="FireBank home">
          <span className="brand-mark">F</span>
          <span>FireBank</span>
        </button>
        <p>Public preview. No custody, deposits, trading, lending, or financial advice is provided by this site.</p>
      </div>
      <div className="footer-links">
        <button type="button" onClick={() => navigate("/trust")}>
          Security
        </button>
        <button type="button" onClick={() => navigate("/legal")}>
          Legal
        </button>
        <button type="button" onClick={() => navigate("/admin")}>
          Admin
        </button>
        <a href="https://github.com/pakmingc/fire">GitHub</a>
      </div>
    </footer>
  );
}

function normalizeRoute(pathname: string): Route {
  const route = pathname === "/index.html" ? "/" : pathname;
  const validRoutes: Route[] = [
    "/",
    "/products/earn",
    "/products/borrow",
    "/products/swap",
    "/products/buy",
    "/products/send",
    "/institutions",
    "/trust",
    "/learn",
    "/support",
    "/company",
    "/legal",
    "/admin"
  ];
  return validRoutes.includes(route as Route) ? (route as Route) : "/";
}

function getRouteTitle(route: Route) {
  const routeLabel: Record<Route, string> = {
    "/": "FireBank | Your Digital Asset Home Base",
    "/products/earn": "Earn Dashboard | FireBank",
    "/products/borrow": "Borrow Flow | FireBank",
    "/products/swap": "Swap Experience | FireBank",
    "/products/buy": "Buy Crypto | FireBank",
    "/products/send": "FireBank Send",
    "/institutions": "Institutions | FireBank",
    "/trust": "Trust & Safety | FireBank",
    "/learn": "Learn | FireBank",
    "/support": "Support | FireBank",
    "/company": "Company | FireBank",
    "/legal": "Legal & Risk | FireBank",
    "/admin": "Admin Console | FireBank"
  };
  return routeLabel[route];
}

export default App;
